import { useState, useEffect } from "react";
import {
  createIncident,
  sendIncident,
  updateIncident,
  deleteIncident,
} from "../../../api/incidents";
import { useNavigate } from "react-router-dom";
import { getLang } from "../../../store/uiStore";
import "./MedicalErrorForm.css";

export default function MedicalErrorForm({
  initialData = null,
  isEdit = false,
  isReadonly = false,
}) {
  const [form, setForm] = useState({
    patient_name: "",
    patient_age: "",
    patient_address: "",
    admission_date: "",
    admission_type: "",
    anamnesis: "",
    diagnosis: "",
    complications: "",
    treatment_actions: "",
    error_type: [],
    error_organization: "",
    error_causes: "",
    pathology_report: "",
  });

  const [incidentId, setIncidentId] = useState(initialData?.id || null);

  const [errors, setErrors] = useState({});

  const navigate = useNavigate();

  const lang = getLang();

  const tr = {
    saved: lang === "ru" ? "Сохранено" : "Сабт шуд",
    sent: lang === "ru" ? "Отправлено" : "Фиристода шуд",
    updated: lang === "ru" ? "Обновлено" : "Навсозӣ шуд",
    deleted: lang === "ru" ? "Удалено" : "Нест карда шуд",

    title:
      lang === "ru"
        ? "Медицинская ошибка"
        : "Хатои тиббӣ",

    patientSection:
      lang === "ru"
        ? "Данные пациента"
        : "Маълумоти бемор",

    hospitalization:
      lang === "ru"
        ? "Госпитализация"
        : "Бистарӣ кардан",

    medicalInfo:
      lang === "ru"
        ? "Медицинская информация"
        : "Маълумоти тиббӣ",

    errorType:
      lang === "ru"
        ? "Тип ошибки"
        : "Навъи хато",

    causes:
      lang === "ru"
        ? "Причины и заключение"
        : "Сабабҳо ва хулоса",

    patientName:
      lang === "ru"
        ? "ФИО пациента"
        : "Ному насаби бемор",

    age:
      lang === "ru"
        ? "Возраст"
        : "Синну сол",

    address:
      lang === "ru"
        ? "Адрес"
        : "Суроға",

    select:
      lang === "ru"
        ? "Выберите"
        : "Интихоб кунед",

    referral:
      lang === "ru"
        ? "По направлению"
        : "Бо роҳхат",

    self:
      lang === "ru"
        ? "Самообращение"
        : "Муроҷиати шахсӣ",

    ambulance:
      lang === "ru"
        ? "Скорая помощь"
        : "Ёрии таъҷилӣ",

    save:
      lang === "ru"
        ? "Сохранить"
        : "Сабт кардан",

    send:
      lang === "ru"
        ? "Отправить"
        : "Фиристодан",

    update:
      lang === "ru"
        ? "Обновить"
        : "Навсозӣ",

    remove:
      lang === "ru"
        ? "Удалить"
        : "Нест кардан",

    anamnesis:
      lang === "ru"
        ? "Анамнез"
        : "Анамнез",

    diagnosis:
      lang === "ru"
        ? "Диагноз"
        : "Ташхис",

    complications:
      lang === "ru"
        ? "Осложнения"
        : "Оқибатҳо",

    treatment:
      lang === "ru"
        ? "Лечение"
        : "Табобат",

    organizational:
      lang === "ru"
        ? "Организационная"
        : "Ташкилотӣ",

    diagnostic:
      lang === "ru"
        ? "Диагностическая"
        : "Ташхисӣ",

    treatmentType:
      lang === "ru"
        ? "Лечебная"
        : "Табобатӣ",

    documentation:
      lang === "ru"
        ? "Документационная"
        : "Ҳуҷҷатгузорӣ",

    whereError:
      lang === "ru"
        ? "Где допущена ошибка"
        : "Хато дар куҷо роҳ дода шуд",

    errorCauses:
      lang === "ru"
        ? "Причины ошибки"
        : "Сабабҳои хато",

    pathology:
      lang === "ru"
        ? "Патолого-анатомическое заключение"
        : "Хулосаи патолого-анатомӣ",

    admissionDate:
      lang === "ru"
        ? "Дата госпитализации"
        : "Санаи бистарӣ",

    admissionType:
      lang === "ru"
        ? "Способ поступления"
        : "Тарзи қабул",
  };

  useEffect(() => {
    if (initialData) {
      const data = initialData.data || {};

      setForm({
        ...data,
        error_type:
          typeof data.error_type === "string"
            ? data.error_type.split(",")
            : data.error_type || [],
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    if (isReadonly) return;

    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiSelect = (value) => {
    if (isReadonly) return;

    setForm((prev) => {
      const exists = prev.error_type.includes(value);
      return {
        ...prev,
        error_type: exists
          ? prev.error_type.filter((v) => v !== value)
          : [...prev.error_type, value],
      };
    });
  };

  const validate = () => {
    const newErrors = {};

    if (!form.patient_name)
      newErrors.patient_name =
        lang === "ru"
          ? "Введите ФИО пациента"
          : "Ному насаби беморро ворид кунед";

    if (!form.patient_age)
      newErrors.patient_age =
        lang === "ru"
          ? "Введите возраст"
          : "Синну солро ворид кунед";

    if (!form.patient_address)
      newErrors.patient_address =
        lang === "ru"
          ? "Введите адрес"
          : "Суроғаро ворид кунед";

    if (!form.admission_date)
      newErrors.admission_date =
        lang === "ru"
          ? "Укажите дату госпитализации"
          : "Санаи бистаришавиро нишон диҳед";

    if (!form.admission_type)
      newErrors.admission_type =
        lang === "ru"
          ? "Выберите тип госпитализации"
          : "Навъи бистаришавиро интихоб кунед";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const save = async () => {
    const payload = {
      type: "medical_error",
      data: {
        ...form,
        error_type: Array.isArray(form.error_type)
          ? form.error_type.join(",")
          : form.error_type,
      },
    };

    if (incidentId) {
      // 🔄 обновление
      await updateIncident(incidentId, payload);
    } else {
      // 🆕 создание
      const res = await createIncident(payload);
      setIncidentId(res.data.id); // ⭐ сохраняем id
      navigate(`/incidents/${res.data.id}`);
    }

    alert(tr.saved);
  };

  const send = async () => {
    if (!validate()) return;

    const payload = {
      type: "medical_error",
      data: {
        ...form,
        error_type: Array.isArray(form.error_type)
          ? form.error_type.join(",")
          : form.error_type,
      },
    };

    if (incidentId) {
      await updateIncident(incidentId, payload);
      await sendIncident(incidentId);
    } else {
      const res = await createIncident(payload);
      setIncidentId(res.data.id);
      await sendIncident(res.data.id);
    }

    alert(tr.sent);
  };

  const update = async () => {
    await updateIncident(initialData.id, {
      type: "medical_error",
      data: {
        ...form,
        error_type: form.error_type.join(","),
      },
    });
    alert("tr.updated");
  };

  const remove = async () => {
    await deleteIncident(initialData.id);
    alert(tr.deleted);
    window.location.href = "/incidents";
  };

  return (
    <div style={styles.container} className="nizom-medical-error-form">
      <h2>{tr.title}</h2>

      {/* Пациент */}
      <div style={styles.section}>
        <h3>{tr.patientSection}</h3>

        <div style={styles.row}>
          <input
            name="patient_name"
            placeholder={tr.patientName}
            value={form.patient_name || ""}
            onChange={handleChange}
            disabled={isReadonly}
            style={styles.input}
          />

          {errors.patient_name && (
            <div style={styles.error}>{errors.patient_name}</div>
          )}

          <input
            name="patient_age"
            placeholder={tr.age}
            value={form.patient_age || ""}
            onChange={handleChange}
            disabled={isReadonly}
            style={styles.input}
          />

          {errors.patient_age && (
            <div style={styles.error}>{errors.patient_age}</div>
          )}
        </div>

        <input
          name="patient_address"
          placeholder={tr.address}
          value={form.patient_address || ""}
          onChange={handleChange}
          disabled={isReadonly}
          style={styles.inputFull}
        />
          {errors.patient_address && (
            <div style={styles.error}>{errors.patient_address}</div>
          )}

      </div>

      {/* Госпитализация */}
      <div style={styles.section}>
        <h3>{tr.hospitalization}</h3>

        <div style={styles.row}>
          <div style={{ flex: 1 }}>
            <label style={styles.label}>
              {tr.admissionDate}
            </label>
            <input
              type="date"
              name="admission_date"
              value={form.admission_date || ""}
              onChange={handleChange}
              disabled={isReadonly}
              style={styles.input}
            />
          </div>
          {errors.admission_date && (
            <div style={styles.error}>{errors.admission_date}</div>
          )}
          <div style={{ flex: 1 }}>
            <label style={styles.label}>
               &nbsp;
            </label>
            <select
              name="admission_type"
              value={form.admission_type || ""}
              onChange={handleChange}
              disabled={isReadonly}
              style={styles.input}
            >
              <option value="">{tr.admissionType}</option>
              <option value="referral">{tr.referral}</option>
              <option value="self">{tr.self}</option>
              <option value="ambulance">{tr.ambulance}</option>
            </select>
          </div>
          {errors.admission_type && (
            <div style={styles.error}>{errors.admission_type}</div>
          )}

        </div>
      </div>

      {/* Медицинская информация */}
      <div style={styles.section}>
        <h3>{tr.medicalInfo}</h3>

        <textarea
          name="anamnesis"
          placeholder={tr.anamnesis}
          value={form.anamnesis || ""}
          onChange={handleChange}
          disabled={isReadonly}
          style={styles.textarea}
        />

        <textarea
          name="diagnosis"
          placeholder={tr.diagnosis}
          value={form.diagnosis || ""}
          onChange={handleChange}
          disabled={isReadonly}
          style={styles.textarea}
        />

        <textarea
          name="complications"
          placeholder={tr.complications}
          value={form.complications || ""}
          onChange={handleChange}
          disabled={isReadonly}
          style={styles.textarea}
        />

        <textarea
          name="treatment_actions"
          placeholder={tr.treatment}
          value={form.treatment_actions || ""}
          onChange={handleChange}
          disabled={isReadonly}
          style={styles.textarea}
        />
      </div>

      {/* Тип ошибки */}
      <div style={styles.section}>
        <h3>{tr.errorType}</h3>

        <div style={styles.checkboxGroup}>
          {[
            { value: "organizational", label: tr.organizational },
            { value: "diagnostic", label: tr.diagnostic },
            { value: "treatment", label: tr.treatmentType },
            { value: "documentation", label: tr.documentation },
          ].map((item) => (
            <label key={item.value} style={styles.checkbox}>
              <input
                type="checkbox"
                checked={(form.error_type || []).includes(item.value)}
                onChange={() => handleMultiSelect(item.value)}
                disabled={isReadonly}
              />
              {item.label}
            </label>
          ))}
        </div>
      </div>

      {/* Причины */}
      <div style={styles.section}>
        <h3>{tr.causes}</h3>

        <input
          name="error_organization"
          placeholder={tr.whereError}
          value={form.error_organization || ""}
          onChange={handleChange}
          disabled={isReadonly}
          style={styles.inputFull}
        />

        <textarea
          name="error_causes"
          placeholder={tr.errorCauses}
          value={form.error_causes || ""}
          onChange={handleChange}
          disabled={isReadonly}
          style={styles.textarea}
        />

        <textarea
          name="pathology_report"
          placeholder={tr.pathology}
          value={form.pathology_report || ""}
          onChange={handleChange}
          disabled={isReadonly}
          style={styles.textarea}
        />
      </div>

      {/* Кнопки */}
      {!isReadonly && (
        <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
          {!isEdit && (
            <>
              <button onClick={save}>{tr.save}</button>
              <button onClick={send}>{tr.send}</button>
            </>
          )}

          {isEdit && (
            <>
              <button onClick={update}>{tr.update}</button>
              <button onClick={send}>{tr.send}</button>
              <button onClick={remove}>{tr.remove}</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* 🎨 СТИЛИ (вернули дизайн) */
const styles = {
  container: {
    width: "100%",
    padding: "20px",
  },

  section: {
    background: "#fff",
    padding: "20px",
    marginBottom: "20px",
    borderRadius: "10px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  },

  row: {
    display: "flex",
    gap: "10px",
    marginBottom: "10px",
  },

  input: {
    flex: 1,
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
  },

  inputFull: {
    width: "100%",
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
  },

  textarea: {
    width: "100%",
    minHeight: "80px",
    marginBottom: "10px",
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
  },

  checkboxGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
  },

  checkbox: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },

  error: {
    color: "red",
    fontSize: "12px",
    marginTop: "4px",
  },
};
